/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { PDFDownloadLink, pdf } from '@react-pdf/renderer';
import { MonthlyInvoicesDocument } from './MonthlyInvoicesDocument';
import moment from 'moment';

interface DailyInvoicePdfExportProps {
  invoices: any[];
  type: 'fbl-delivery' | 'general-delivery' | 'inventory-sale' | 'platform-fee';
  filePrefix: string;
}

export function DailyInvoicePdfExport({ invoices, type, filePrefix }: DailyInvoicePdfExportProps) {
  const [selectedDate, setSelectedDate] = useState<string>('ALL');
  const [isExportingAll, setIsExportingAll] = useState(false);
  const [exportProgress, setExportProgress] = useState<string>('');

  if (!invoices || invoices.length === 0) return null;

  // Group invoices by date string (YYYY-MM-DD)
  const groupedByDay: { [dateStr: string]: any[] } = {};
  invoices.forEach((inv: any) => {
    const dateKey = inv.invoice_date ? moment.utc(inv.invoice_date).format('YYYY-MM-DD') : 'Unknown_Date';
    if (!groupedByDay[dateKey]) {
      groupedByDay[dateKey] = [];
    }
    groupedByDay[dateKey].push(inv);
  });

  const availableDates = Object.keys(groupedByDay).sort();

  const handleExportAllDays = async () => {
    if (isExportingAll) return;
    setIsExportingAll(true);

    for (let i = 0; i < availableDates.length; i++) {
      const dateKey = availableDates[i];
      const dayInvoices = groupedByDay[dateKey];
      setExportProgress(`Exporting ${i + 1}/${availableDates.length} (${dateKey})...`);

      try {
        const doc = <MonthlyInvoicesDocument invoices={dayInvoices} type={type} />;
        const blob = await pdf(doc).toBlob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filePrefix}_Invoices_${dateKey}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error(`Failed to export PDF for ${dateKey}`, err);
      }

      // Small pause between downloads to let browser process cleanly
      await new Promise(r => setTimeout(r, 400));
    }

    setIsExportingAll(false);
    setExportProgress('');
  };

  const selectedInvoices = selectedDate !== 'ALL' ? groupedByDay[selectedDate] || [] : [];

  return (
    <div className="form-inline mr-2">
      <select
        className="form-control form-control-sm mr-2"
        value={selectedDate}
        onChange={e => setSelectedDate(e.target.value)}
        disabled={isExportingAll}
      >
        <option value="ALL">
          All Days ({invoices.length} invoices across {availableDates.length} days)
        </option>
        {availableDates.map(dateKey => (
          <option key={dateKey} value={dateKey}>
            {dateKey} ({groupedByDay[dateKey].length} invoice{groupedByDay[dateKey].length > 1 ? 's' : ''})
          </option>
        ))}
      </select>

      {selectedDate === 'ALL' ? (
        <button
          className="btn btn-primary btn-sm"
          type="button"
          onClick={handleExportAllDays}
          disabled={isExportingAll}
        >
          {isExportingAll ? exportProgress || 'Exporting...' : 'Export All Days (Day-by-Day)'}
        </button>
      ) : (
        <PDFDownloadLink
          className="btn btn-primary btn-sm"
          document={<MonthlyInvoicesDocument invoices={selectedInvoices} type={type} />}
          fileName={`${filePrefix}_Invoices_${selectedDate}.pdf`}
        >
          {({ loading }) => (loading ? 'Preparing PDF...' : `Export PDF (${selectedDate})`)}
        </PDFDownloadLink>
      )}
    </div>
  );
}
